// CoachDesk: Eltern/Spieler App-Einladungen (OTP + TrainingsDesk-Zugang).

import { getSession } from "../auth/index.js";
import { getSupabaseClient } from "../supabase/index.js";
import {
  isMissingRestResourceError,
  isRestEndpointUnavailable,
  markRestEndpointUnavailable,
} from "../supabase/rest-endpoint-availability.js";
import {
  isHubReadScopeCurrent,
  registerHubCacheClear,
  resolveHubReadScope,
} from "../hub/hub-scoped-cache.js";

const RPC_CONTACT_APP_REGISTRATION_STATUS =
  "rpc:trainerstunden_contact_app_registration_status";

function __asStr(x) {
  return String(x ?? "").trim();
}

/** @param {{ user_metadata?: Record<string, unknown> } | null | undefined} user */
export function mustSetPasswordFromAuthUser(user) {
  const meta =
    user?.user_metadata && typeof user.user_metadata === "object"
      ? user.user_metadata
      : {};
  const v = meta.must_set_password;
  return v === true || v === "true" || v === 1;
}

const ACCEPTED_INVITE_TENANT_IDS_CACHE_MS = 20000;
/** @type {Map<string, Promise<{ok: boolean, reason?: string, tenantIds: string[], error?: any}>>} */
const __acceptedInviteTenantIdsInflight = new Map();
/** @type {Map<string, { ts: number, value: {ok: boolean, reason?: string, tenantIds: string[], error?: any} }>} */
const __acceptedInviteTenantIdsCache = new Map();

export function clearMyAcceptedContactAppInviteTenantIdsCache() {
  __acceptedInviteTenantIdsCache.clear();
  __acceptedInviteTenantIdsInflight.clear();
}

registerHubCacheClear(clearMyAcceptedContactAppInviteTenantIdsCache);

export async function loadMyAcceptedContactAppInviteTenantIdsRemote(
  options = {},
) {
  const force = options?.force === true;
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase", tenantIds: [] };

    const scope = await resolveHubReadScope();
    const cacheKey = scope.key;
    const now = Date.now();
    if (!force) {
      const cached = __acceptedInviteTenantIdsCache.get(cacheKey);
      if (cached && now - cached.ts < ACCEPTED_INVITE_TENANT_IDS_CACHE_MS) {
        return cached.value;
      }
      const inflight = __acceptedInviteTenantIdsInflight.get(cacheKey);
      if (inflight) return inflight;
    }

    const started = { ...scope };
    const run = (async () => {
      const { data, error } = await supabase.rpc(
        "trainerstunden_get_my_accepted_contact_app_invite_tenant_ids",
      );
      if (error)
        return { ok: false, reason: "rpc-error", error, tenantIds: [] };
      if (!data || typeof data !== "object")
        return { ok: false, reason: "no-data", tenantIds: [] };
      if (data.ok === false)
        return {
          ok: false,
          reason: __asStr(data.reason) || "unknown-error",
          tenantIds: [],
        };

      const tenantIds = (Array.isArray(data.tenant_ids) ? data.tenant_ids : [])
        .map((id) => __asStr(id))
        .filter(Boolean);
      return { ok: true, tenantIds };
    })();

    const promise = run
      .then((value) => {
        if (value?.ok && isHubReadScopeCurrent(started)) {
          __acceptedInviteTenantIdsCache.set(cacheKey, {
            ts: Date.now(),
            value,
          });
        }
        return value;
      })
      .finally(() => {
        if (__acceptedInviteTenantIdsInflight.get(cacheKey) === promise) {
          __acceptedInviteTenantIdsInflight.delete(cacheKey);
        }
      });
    __acceptedInviteTenantIdsInflight.set(cacheKey, promise);
    return promise;
  } catch (e) {
    return { ok: false, reason: "exception", error: e, tenantIds: [] };
  }
}

const CONTACT_INVITE_TOKEN_STORAGE_KEY = "tm_contact_app_invite_token";

/**
 * Einladungs-Annahme erzwingen, solange Passwort/Einladung offen ist.
 * @returns {Promise<string|null>}
 */
export async function resolvePendingContactAppInviteAcceptPathRemote() {
  try {
    const stored = __asStr(
      typeof sessionStorage !== "undefined"
        ? sessionStorage.getItem(CONTACT_INVITE_TOKEN_STORAGE_KEY)
        : "",
    );
    if (stored) {
      return `/trainingsdesk/einladung?token=${encodeURIComponent(stored)}`;
    }
  } catch (_) {
    // noop
  }

  try {
    const session = await getSession();
    if (session?.user && mustSetPasswordFromAuthUser(session.user)) {
      const pending = await getMyPendingContactAppInviteRemote();
      if (pending?.ok && pending.token) {
        return `/trainingsdesk/einladung?token=${encodeURIComponent(pending.token)}`;
      }
      return "/trainingsdesk/einladung";
    }
  } catch (_) {
    // noop
  }

  try {
    const pending = await getMyPendingContactAppInviteRemote();
    if (pending?.ok && pending.token) {
      return `/trainingsdesk/einladung?token=${encodeURIComponent(pending.token)}`;
    }
  } catch (_) {
    // noop
  }

  return null;
}

export async function getMyPendingContactAppInviteRemote() {
  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_get_my_pending_contact_app_invite",
    );
    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return {
      ok: true,
      token: __asStr(data.token),
      inviteEmail: __asStr(data.invite_email),
      status: __asStr(data.status),
    };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

/**
 * @param {string[]} personIds
 */
export async function loadContactAppRegistrationStatusRemote(personIds) {
  const ids = (Array.isArray(personIds) ? personIds : [])
    .map(__asStr)
    .filter(Boolean);
  if (!ids.length) return { ok: true, items: [] };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    if (isRestEndpointUnavailable(RPC_CONTACT_APP_REGISTRATION_STATUS)) {
      return {
        ok: true,
        items: [],
        skipped: true,
        reason: "rpc-unavailable-cached",
      };
    }

    const { data, error } = await supabase.rpc(
      "trainerstunden_contact_app_registration_status",
      {
        p_person_ids: ids,
      },
    );

    if (error) {
      if (isMissingRestResourceError(error)) {
        markRestEndpointUnavailable(RPC_CONTACT_APP_REGISTRATION_STATUS);
        return { ok: true, items: [], skipped: true, reason: "rpc-missing" };
      }
      return { ok: false, reason: "rpc-error", error };
    }
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };

    const items = Array.isArray(data.items) ? data.items : [];
    return { ok: true, items };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function prepareContactAppInviteRemote(personId) {
  const pid = __asStr(personId);
  if (!pid) return { ok: false, reason: "missing-person-id" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_prepare_contact_app_invite",
      {
        p_person_id: pid,
      },
    );

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return {
        ok: false,
        reason: __asStr(data.reason) || "unknown-error",
        email: __asStr(data.email),
      };
    return { ok: true, payload: data };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function getContactAppInviteByTokenRemote(token) {
  const t = __asStr(token);
  if (!t) return { ok: false, reason: "missing-token" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_get_contact_app_invite_by_token",
      {
        p_token: t,
      },
    );

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, invite: data };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function acceptContactAppInviteRemote(token) {
  const t = __asStr(token);
  if (!t) return { ok: false, reason: "missing-token" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_accept_contact_app_invite",
      {
        p_token: t,
      },
    );

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true, result: data };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export async function markContactAppInvitePasswordSetRemote(token) {
  const t = __asStr(token);
  if (!t) return { ok: false, reason: "missing-token" };

  try {
    const supabase = await getSupabaseClient();
    if (!supabase) return { ok: false, reason: "no-supabase" };

    const { data, error } = await supabase.rpc(
      "trainerstunden_mark_contact_app_invite_password_set",
      {
        p_token: t,
      },
    );

    if (error) return { ok: false, reason: "rpc-error", error };
    if (!data || typeof data !== "object")
      return { ok: false, reason: "no-data" };
    if (data.ok === false)
      return { ok: false, reason: __asStr(data.reason) || "unknown-error" };
    return { ok: true };
  } catch (e) {
    return { ok: false, reason: "exception", error: e };
  }
}

export function resolveContactAppInviteErrorKey(reason) {
  switch (__asStr(reason)) {
    case "already-registered":
      return "trainerstunden.contactAppInvite.errors.alreadyRegistered";
    case "missing-email":
      return "trainerstunden.contactAppInvite.errors.missingEmail";
    case "missing-tenant":
      return "trainerstunden.contactAppInvite.errors.missingTenant";
    case "not-authorized":
      return "trainerstunden.contactAppInvite.errors.notAuthorized";
    case "no-auth":
    case "invalid-token":
    case "missing-bearer-token":
      return "trainerstunden.contactAppInvite.errors.sessionExpired";
    case "email-mismatch":
      return "trainerstunden.contactAppInvite.errors.emailMismatch";
    case "password-required":
      return "trainerstunden.contactAppInvite.accept.passwordRequired";
    case "invite-expired":
      return "trainerstunden.contactAppInvite.errors.expired";
    case "invite-not-found":
      return "trainerstunden.contactAppInvite.errors.notFound";
    case "prepare-failed":
    case "insert-failed":
    case "rpc-error":
      return "trainerstunden.contactAppInvite.errors.prepareFailed";
    case "mail-failed":
    case "smtp-not-configured":
      return "trainerstunden.contactAppInvite.errors.mailFailed";
    case "auth-create-failed":
    case "auth-update-failed":
    case "auth-user-not-found":
      return "trainerstunden.contactAppInvite.errors.authFailed";
    case "missing-backend-url":
    case "backend-unreachable":
    case "pages-mail-not-configured":
    case "missing-config":
      return "trainerstunden.contactAppInvite.errors.pagesMailNotConfigured";
    case "missing-supabase-url":
      return "trainerstunden.contactAppInvite.errors.missingSupabaseUrl";
    case "missing-supabase-anon-key":
      return "trainerstunden.contactAppInvite.errors.missingSupabaseAnonKey";
    case "missing-supabase-service-key":
      return "trainerstunden.contactAppInvite.errors.missingSupabaseServiceKey";
    case "missing-public-origin":
      return "trainerstunden.contactAppInvite.errors.missingPublicOrigin";
    case "missing-smtp-from":
      return "trainerstunden.contactAppInvite.errors.missingSmtpFrom";
    case "method-not-allowed":
      return "trainerstunden.contactAppInvite.errors.sendFailed";
    default:
      return "trainerstunden.contactAppInvite.errors.sendFailed";
  }
}

/**
 * @param {string} status
 */
export function contactAppRegistrationStatusI18nKey(status) {
  const s = __asStr(status);
  if (s === "registered")
    return "trainerstunden.contactAppInvite.status.registered";
  if (s === "accepted")
    return "trainerstunden.contactAppInvite.status.accepted";
  if (s === "invited") return "trainerstunden.contactAppInvite.status.invited";
  if (s === "no_email") return "trainerstunden.contactAppInvite.status.noEmail";
  return "trainerstunden.contactAppInvite.status.none";
}

export function formatContactAppInviteTimestampDe(iso) {
  const s = __asStr(iso);
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * @param {(key: string, params?: Record<string, unknown>) => string} t
 * @param {{ status?: string, invited_at?: string, accepted_at?: string, last_sign_in_at?: string } | null | undefined} appReg
 * @returns {{ headline: string, metaLines: Array<{ key: string, text: string }> }}
 */
export function resolveContactAppInviteStatusDisplay(t, appReg) {
  const status = __asStr(appReg?.status) || "none";
  const invitedAt = formatContactAppInviteTimestampDe(appReg?.invited_at);
  const acceptedAt = formatContactAppInviteTimestampDe(appReg?.accepted_at);
  const lastSignInAt = formatContactAppInviteTimestampDe(
    appReg?.last_sign_in_at,
  );

  if (status === "invited" && invitedAt) {
    return {
      headline: t("trainerstunden.contactAppInvite.status.invitedAt", {
        date: invitedAt,
      }),
      metaLines: [],
    };
  }

  const headline = t(contactAppRegistrationStatusI18nKey(status));
  const metaLines = [];

  if (invitedAt && (status === "accepted" || status === "registered")) {
    metaLines.push({
      key: "invited",
      text: t("trainerstunden.contactAppInvite.status.invitedAt", {
        date: invitedAt,
      }),
    });
  }
  if (acceptedAt && (status === "accepted" || status === "registered")) {
    metaLines.push({
      key: "accepted",
      text: t("trainerstunden.contactAppInvite.status.acceptedAt", {
        date: acceptedAt,
      }),
    });
  }
  if (status === "registered") {
    if (lastSignInAt) {
      metaLines.push({
        key: "lastLogin",
        text: t("trainerstunden.contactAppInvite.status.lastLoginAt", {
          date: lastSignInAt,
        }),
      });
    } else {
      metaLines.push({
        key: "lastLogin",
        text: t("trainerstunden.contactAppInvite.status.noLoginYet"),
      });
    }
  }

  return { headline, metaLines };
}

/**
 * @param {(key: string, params?: Record<string, unknown>) => string} t
 * @param {{ status?: string, invited_at?: string, accepted_at?: string } | null | undefined} appReg
 */
export function formatContactAppInviteStatusLabel(t, appReg) {
  const { headline, metaLines } = resolveContactAppInviteStatusDisplay(
    t,
    appReg,
  );
  if (!metaLines.length) return headline;
  return headline;
}
